import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import VirtualizedSelect from 'react-virtualized-select';
import { Flag } from '@lightspeed/flame/Flag';
import Group from '@lightspeed/cirrus-group';
import { arrowRenderer, clearRenderer } from '@lightspeed/cirrus-autocomplete';
import { getCountryList } from './utils';

const flagMap = {
  HL: 'SH-HL',
};

const baseClass = 'cr-dropdown';
const dropdownClass = 'cr-dropdown-country';

const countries = getCountryList();

const CountryOptionRenderer = ({
  focusedOption,
  focusOption,
  key,
  option,
  selectValue,
  style,
  valueArray,
}) => {
  const { countryCode, countryName } = option;
  const classNames = cn('Select-option', `${baseClass}__option`, {
    'is-focused': option === focusedOption,
    'is-selected': valueArray.indexOf(option) >= 0,
  });

  return (
    // We follow the rule, but it still fails, see:
    // https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-static-element-interactions.md#case-this-element-is-not-a-button-link-menuitem-etc-it-is-catching-bubbled-events-from-elements-that-it-contains
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      key={key}
      className={classNames}
      style={style}
      onClick={() => selectValue(option)}
      onMouseEnter={() => focusOption(option)}
      role="presentation"
    >
      <Group>
        <Flag code={flagMap[countryCode] || countryCode} />
        <var>{countryName}</var>
      </Group>
    </div>
  );
};

const OptionPropType = PropTypes.shape({
  countryCode: PropTypes.string,
  countryName: PropTypes.string,
}).isRequired;
CountryOptionRenderer.propTypes = {
  key: PropTypes.string.isRequired,
  option: OptionPropType,
  focusedOption: OptionPropType,
  focusOption: PropTypes.func.isRequired,
  selectValue: PropTypes.func.isRequired,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  valueArray: PropTypes.arrayOf(OptionPropType),
};

class CountryDropdown extends Component {
  constructor(props) {
    super(props);

    const { countryCode, countryName } = this.props;

    this.useCustomCountryName = false;

    if (countryCode && countryName) {
      const countryWithCustomNameIndex = countries.findIndex(
        country => country.countryCode === countryCode && country.countryName !== countryName
      );

      if (countryWithCustomNameIndex !== -1) {
        countries[countryWithCustomNameIndex].customCountryName = countryName;
        this.useCustomCountryName = true;
      }
    }

    this.selectRef = React.createRef();

    this.handleFlagClick = this.handleFlagClick.bind(this);
  }

  handleFlagClick() {
    this.selectRef.current.focus();
  }

  render() {
    const {
      onSelectChange,
      onInputChange,
      countryName,
      countryCode,
      isNotranslate = true,
      placeholder = 'Select a Country',
      className,
      ...rest
    } = this.props;

    let selectClass = cn(baseClass, dropdownClass, className);
    let customPlaceholder = placeholder;

    // @Note: React-select does not allow for setting a default outside of options
    // Instead trick the placeholder to display the custom countryName
    if (!countryCode && countryName) {
      customPlaceholder = countryName;
      selectClass = cn(selectClass, 'cr-dropdown--zz-placeholder');
    }

    const noTranslateProps = isNotranslate
      ? {
          valueRenderer: option => (
            <var>{this.useCustomCountryName ? option.customCountryName : option.countryName}</var>
          ),
        }
      : {};

    return (
      <div className={`${dropdownClass}__wrapper`} data-test="country-dropdown">
        <VirtualizedSelect
          className={selectClass}
          {...rest}
          {...noTranslateProps}
          labelKey="countryName"
          valueKey="countryCode"
          placeholder={customPlaceholder}
          optionRenderer={CountryOptionRenderer}
          optionHeight={36}
          options={countries}
          onBlurResetsInput={false}
          onCloseResetsInput={false}
          noResultsText={null}
          value={countryCode}
          arrowRenderer={arrowRenderer}
          clearRenderer={clearRenderer}
          ref={this.selectRef}
          onChange={option => {
            this.useCustomCountryName = false;
            onSelectChange(option);
          }}
          onInputChange={onInputChange}
          onFocus={e => {
            const TOP_BAR_HEIGHT = 66;
            const isTouch = 'ontouchstart' in document.documentElement;

            // On touch devices, we want to scroll to the top of the input when focusing,
            // else in most cases the keyboard will overlay the dropdown menu.
            if (isTouch) {
              const elementPosition = e.target.getBoundingClientRect();
              window.scroll({
                top: elementPosition.top + window.scrollY - TOP_BAR_HEIGHT,
              });
            }
          }}
        />

        <div // eslint-disable-line
          className={`${baseClass}__flag`}
          onClick={this.handleFlagClick}
          role="button"
          tabIndex="-1"
        >
          <span>
            <Flag code={countryCode ? flagMap[countryCode] || countryCode : 'ZZ'} />
          </span>
        </div>
      </div>
    );
  }
}

CountryDropdown.defaultProps = {
  onSelectChange: () => {},
};

CountryDropdown.propTypes = {
  placeholder: PropTypes.string,
  countryCode: PropTypes.string,
  countryName: PropTypes.string,
  initialCountryName: PropTypes.string,
  initialCountryCode: PropTypes.string,
  onSelectChange: PropTypes.func,
  onInputChange: PropTypes.func,
  onBlur: PropTypes.func,
  isNotranslate: PropTypes.bool,
  className: PropTypes.string,
};

export default CountryDropdown;
